import { type NetWorthSeries, type NetWorthSeriesQuery, reportingKeys } from '@taler/api-client'
import { formatLocalDate, systemClock, todayUtc } from '@taler/domain'
import { useQuery } from '@tanstack/vue-query'
import { computed, type ComputedRef } from 'vue'
import { useApi } from '@/app/api'
import { retrying } from '@/app/retrying'

export interface UseNetWorthHistoryOptions {
  readonly ledgerId: () => string | undefined
  readonly displayCurrency: () => string
  readonly enabled: () => boolean
  /** Undefined is the whole history up to `until`. */
  readonly from?: () => string | undefined
  /** Defaults to today. */
  readonly until?: () => string
}

export interface UseNetWorthHistory {
  readonly data: ComputedRef<NetWorthSeries | undefined>
  readonly isLoading: ComputedRef<boolean>
  readonly error: ComputedRef<Error | null>
  readonly retrying: ComputedRef<boolean>
  retry(): void
}

/** Net worth series for one ledger and currency - not the dashboard bundle. The dashboard and the Reports chart share it. */
export function useNetWorthHistory(options: UseNetWorthHistoryOptions): UseNetWorthHistory {
  const api = useApi()
  const today = formatLocalDate(todayUtc(systemClock))
  const active = computed(() => options.enabled() && options.ledgerId() !== undefined)

  const query = computed<NetWorthSeriesQuery>(() => {
    const from = options.from?.()
    return {
      until: options.until?.() ?? today,
      displayCurrency: options.displayCurrency() as NetWorthSeriesQuery['displayCurrency'],
      ...(from === undefined ? {} : { from }),
    }
  })

  const historyQuery = useQuery({
    queryKey: computed(() => reportingKeys.netWorthSeries(options.ledgerId() ?? '', query.value)),
    queryFn: ({ signal }) => api.reporting.netWorthSeries(options.ledgerId()!, query.value, signal),
    enabled: active,
    retry: false,
  })

  return {
    data: computed(() => historyQuery.data.value),
    isLoading: computed(() => active.value && historyQuery.isPending.value),
    error: computed(() => historyQuery.error.value),
    retrying: retrying(historyQuery),
    retry: () => {
      void historyQuery.refetch()
    },
  }
}
